'use client';
// An offline document hands back to its online screen once the server answers again (BL-2343,
// ADR 066 — Offline Mode § 6.1 limit 4): online, the session and the screen come from the server,
// never from the device store.
import { useEffect } from 'react';
import { REACHABILITY_PROBE_PATH } from '@auctogon/core/connection-health';
import { onlinePathForOfflineDocument } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';

import { confirmOriginWithOfflineBoot } from './offline-boot-registration';
import { readManualOfflineFlag } from './manual-offline-flag';

/**
 * Per-tab note of the last hand-back, so a server that answers the probe but still fails the page
 * (the worker would serve this document again) cannot bounce the tab between the two. UI state,
 * never server state (rule #16).
 */
export const HAND_BACK_STORAGE_KEY = 'auctogon:offline-document:handed-back-at';

/** How long after a hand-back the document stays put rather than hand back again. */
export const HAND_BACK_COOLDOWN_MS = 60_000;

/**
 * How soon to check again when the server, or the offline-boot worker, cannot be reached yet.
 * Each further retry waits twice as long, up to {@link HAND_BACK_COOLDOWN_MS}.
 */
export const HAND_BACK_RETRY_MS = 3_000;

const PROBE_TIMEOUT_MS = 5_000;

/** The hand-back navigation. A replace, so Back does not return to the offline document. */
let navigate = (url: string): void => window.location.replace(url);

/**
 * Test seam: jsdom cannot navigate, so a test records the hand-back instead.
 *
 * @param next - the navigation to use, or null to restore `location.replace`.
 */
export function __setHandBackNavigationForTest(next: ((url: string) => void) | null): void {
  navigate = next ?? ((url: string) => window.location.replace(url));
}

/** @returns 'blocked' when session storage is unusable (no loop guard), else whether a hand-back is cooling down. */
function handBackCooldown(now: number): 'blocked' | 'cooling' | 'clear' {
  try {
    const at = Number(window.sessionStorage.getItem(HAND_BACK_STORAGE_KEY));
    return Number.isFinite(at) && now - at < HAND_BACK_COOLDOWN_MS ? 'cooling' : 'clear';
  } catch {
    // Blocked storage: no loop guard, so stay on the offline document and never poll (BUG-2678).
    return 'blocked';
  }
}

/**
 * Whether the server answers with a success. Unlike the connectivity probe, a 5xx counts as no
 * server here, because the service worker falls back on one (ADR 066 — Offline Mode § 1).
 */
async function serverAnswers(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(REACHABILITY_PROBE_PATH, { cache: 'no-store', signal: controller.signal });
    return response.ok;
  } catch {
    // A rejected probe is the definition of no connection; nothing to report.
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * What one hand-back check came to: `retry` when the server or the worker could not be reached yet
 * (or a hand-back is still cooling down), `stayed` when the document must stay (manual offline, no
 * storage for the loop guard).
 */
type HandBackOutcome = 'stayed' | 'retry' | 'handed-back';

async function handBackIfOnline(): Promise<HandBackOutcome> {
  if (readManualOfflineFlag()) return 'stayed'; // the operator chose offline; stay
  // BUG-2678: both are 'retry', never a silent stop. The network is often not usable the moment the
  // `online` event fires and no second event follows, and a document opened inside a cooldown has
  // no other trigger once it ends. A hand-back that gave up here held the app on "No connection"
  // with its queue unsent for good.
  const cooldown = handBackCooldown(Date.now());
  if (cooldown === 'blocked') return 'stayed';
  if (cooldown === 'cooling' || !(await serverAnswers())) return 'retry';
  // The worker carries the navigation and, after a failure, serves this document for 30 s without
  // asking the network. Hand back only once it reaches the origin too, and before stamping the
  // cooldown, or the bounced navigation would hold the tab here while online (BUG-2052).
  if ((await confirmOriginWithOfflineBoot(window.navigator.serviceWorker)) === 'unreachable') return 'retry';
  try {
    window.sessionStorage.setItem(HAND_BACK_STORAGE_KEY, String(Date.now()));
  } catch {
    return 'stayed'; // no loop guard, so no hand-back
  }
  navigate(onlinePathForOfflineDocument(window.location));
  return 'handed-back';
}

/**
 * On an offline document: check the server on mount, when the device comes back online, and on
 * every return to the foreground, and replace the document with its online screen once it answers.
 * When the page does not reach the server, or reaches it but the offline-boot worker does not yet,
 * or a hand-back is cooling down, check again after {@link HAND_BACK_RETRY_MS}, backing off to once
 * a minute (BUG-2678).
 *
 * @sideEffect Issues an uncached `GET /api/time`; asks the offline-boot worker to confirm the origin;
 *   may navigate.
 */
export function useHandBackWhenOnline(): void {
  useEffect(() => {
    let live = true;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let retryDelay = HAND_BACK_RETRY_MS;
    const check = () => {
      void handBackIfOnline().then((outcome) => {
        if (!live) return;
        if (outcome !== 'retry') {
          retryDelay = HAND_BACK_RETRY_MS;
          return;
        }
        clearTimeout(retry);
        retry = setTimeout(check, retryDelay);
        retryDelay = Math.min(retryDelay * 2, HAND_BACK_COOLDOWN_MS);
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    check();
    window.addEventListener('online', check);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      live = false;
      clearTimeout(retry);
      window.removeEventListener('online', check);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}
